import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { full as emoji } from 'markdown-it-emoji';
import subscript from 'markdown-it-sub';
import superscript from 'markdown-it-sup';

const options = { breaks: true, linkify: false, typographer: false };
const markdown = new MarkdownIt({ ...options, html: false });
const legacyMarkdown = new MarkdownIt({ ...options, html: true });

/** Highlight is an extension to Markdown; pair delimiters like emphasis, never inside code. */
function enableHighlight(renderer: MarkdownIt) {
    renderer.inline.ruler.before('emphasis', 'highlight', (state, silent) => {
        if (silent || state.src.charCodeAt(state.pos) !== 61 || state.src.charCodeAt(state.pos - 1) === 61) return false;
        const scanned = state.scanDelims(state.pos, true);
        if (scanned.length !== 2) return false;
        const token = state.push('text', '', 0);
        token.content = '==';
        state.delimiters.push({ marker: 61, length: 0, token: state.tokens.length - 1,
            end: -1, open: scanned.can_open, close: scanned.can_close });
        state.pos += 2;
        return true;
    });
    renderer.inline.ruler2.before('emphasis', 'highlight', state => {
        const process = (delimiters: typeof state.delimiters) => {
            for (const start of delimiters) {
                if (start.marker !== 61 || start.end < 0) continue;
                const open = state.tokens[start.token];
                const close = state.tokens[delimiters[start.end].token];
                open.type = 'mark_open'; open.tag = 'mark'; open.nesting = 1; open.markup = '=='; open.content = '';
                close.type = 'mark_close'; close.tag = 'mark'; close.nesting = -1; close.markup = '=='; close.content = '';
            }
        };
        process(state.delimiters);
        state.tokens_meta.forEach(meta => { if (meta?.delimiters) process(meta.delimiters); });
        return true;
    });
}

enableHighlight(markdown);
enableHighlight(legacyMarkdown);
for (const renderer of [markdown, legacyMarkdown]) {
    renderer.use(emoji, { shortcuts: {} }).use(subscript).use(superscript);
}

/** One renderer for React views and generated lesson documents. */
export function markdownHtml(source: string | null | undefined, inline = false, legacyHtml = false): string {
    const renderer = legacyHtml ? legacyMarkdown : markdown;
    const html = inline ? renderer.renderInline(source ?? '') : renderer.render(source ?? '');
    const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    const doc = new DOMParser().parseFromString(clean, 'text/html');
    if (inline) doc.body.querySelectorAll('img').forEach(image => image.replaceWith(doc.createTextNode(image.alt)));
    // Compact labels must contain phrasing content, and must not nest links in a label.
    if (inline) {
        doc.body.querySelectorAll('*').forEach(element => {
            if (!['STRONG', 'EM', 'S', 'CODE', 'BR', 'DEL', 'MARK', 'SUB', 'SUP'].includes(element.tagName)) {
                element.replaceWith(...Array.from(element.childNodes));
            }
        });
    } else {
        doc.body.querySelectorAll('a[href]').forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });
    }
    return doc.body.innerHTML.trim();
}

export function markdownText(source: string | null | undefined, legacyHtml = false): string {
    const doc = new DOMParser().parseFromString(markdownHtml(source, false, legacyHtml), 'text/html');
    doc.body.querySelectorAll('img').forEach(image => image.replaceWith(doc.createTextNode(image.alt)));
    doc.body.querySelectorAll('br').forEach(node => node.replaceWith(doc.createTextNode('\n')));
    doc.body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, tr').forEach(node => node.append('\n'));
    return (doc.body.textContent ?? '').trim();
}

export const MARKDOWN_STYLES = `
.markdown-content { min-width:0; max-width:100%; overflow-wrap:anywhere; line-height:1.6; white-space:normal; }
.markdown-content > :first-child { margin-top:0; }
.markdown-content > :last-child { margin-bottom:0; }
.markdown-content p { margin:.65em 0; }
.markdown-content h1, .markdown-content h2, .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6 { line-height:1.3; margin:1em 0 .5em; font-weight:700; }
.markdown-content h1 { font-size:1.5em; } .markdown-content h2 { font-size:1.3em; } .markdown-content h3 { font-size:1.15em; }
.markdown-content h4, .markdown-content h5, .markdown-content h6 { font-size:1em; }
.markdown-content ul, .markdown-content ol { margin:.65em 0; padding-left:1.5em; }
.markdown-content li + li { margin-top:.25em; }
.markdown-content blockquote { margin:.75em 0; padding:.25em 1em; border-left:3px solid var(--surface-border,#999); }
.markdown-content code { font-size:.9em; background:var(--surface-ground,#eee); color:var(--text-color,inherit); padding:.1em .25em; border-radius:.2em; }
.markdown-content pre { max-width:100%; overflow:auto; white-space:pre; padding:.75em; background:var(--surface-ground,#eee); border-radius:.25em; }
.markdown-content pre code { padding:0; background:none; }
.markdown-content img { max-width:100%; height:auto; }
.markdown-content table { display:block; max-width:100%; overflow:auto; border-collapse:collapse; }
.markdown-content th, .markdown-content td { border:1px solid var(--surface-border,#999); padding:.4em .6em; text-align:left; }
.markdown-content a { color:var(--primary-color,#2563eb); text-decoration:underline; }
.markdown-content hr { border:0; border-top:1px solid var(--surface-border,#999); margin:1em 0; }
.markdown-inline { overflow-wrap:anywhere; white-space:normal; }
.markdown-inline code { font-size:.9em; }
.markdown-content mark, .markdown-inline mark { background:#fef08a; color:#1f2937; border-radius:.15em; padding:0 .12em; box-decoration-break:clone; -webkit-box-decoration-break:clone; }
.markdown-content sub, .markdown-inline sub, .markdown-content sup, .markdown-inline sup { font-size:.75em; line-height:0; position:static; }
.markdown-content sub, .markdown-inline sub { vertical-align:sub; }
.markdown-content sup, .markdown-inline sup { vertical-align:super; }
`;
